import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  postMerchantsPublicIDEmployeesEmployeePublicIDSendAccess,
  type SendEmployeeAccessRequest,
  type PostMerchantsPublicIDEmployeesEmployeePublicIDSendAccessFetchResponse,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { merchantEmployeeKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployee";
import { merchantEmployeesKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployees";
import { toast } from "sonner";
import { useResource, Resources } from "Translations/Resources";

const RESOURCES = Resources.Hooks.Employee;

type Parameters = {
  data: SendEmployeeAccessRequest;
  publicID: string;
  employeePublicID: string;
};

// The thrown error is a non-200 member of the generated response union — 404/409 carry
// ApiProblemDetails, network/5xx is an ErrorResponse. Derived from the generated type, not hand-rolled.
export type SendEmployeeAccessError = Exclude<
  PostMerchantsPublicIDEmployeesEmployeePublicIDSendAccessFetchResponse,
  { status: 200 }
>;

type Props = {
  onSuccess?: () => void;
  onError?: (error: SendEmployeeAccessError) => void;
};

export const useSendEmployeeAccessMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ data, publicID, employeePublicID }: Parameters) =>
        postMerchantsPublicIDEmployeesEmployeePublicIDSendAccess(
          data,
          publicID,
          employeePublicID,
        ),
    ),
    onSuccess: (_: unknown, { publicID, employeePublicID }: Parameters) => {
      // The list page queries without a merchant id, so invalidate every list, not list(publicID).
      queryClient.invalidateQueries({
        queryKey: merchantEmployeesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: merchantEmployeeKeys.singleEmployee(
          publicID,
          employeePublicID,
        ),
      });
      toast.success(t(RESOURCES.AccessSent));
      onSuccess?.();
    },
    // Error presentation belongs to the caller — the single consumer surfaces these inline (the same
    // Alert as the pre-send identity-lookup check), so both failure paths look identical.
    onError,
  });
};
